import React from 'react';
import { Alert } from '@/shared/components';
import { Button } from '@/shared/components/ui/button/Button';
import type {
    DuplicateInScopeError,
    DuplicateInScopeExistingCustomer,
} from '../../services/customers';

// Candidates come from the 422 payload; Select is gated on `customer.read` because it leads to a read-only page.

interface DuplicateCandidateRowProps {
    candidate: DuplicateInScopeExistingCustomer;
    canReadCustomers: boolean;
    onSelect: () => void;
}

const DuplicateCandidateRow: React.FC<DuplicateCandidateRowProps> = ({
    candidate,
    canReadCustomers,
    onSelect,
}) => {
    const nameParts = [candidate.forename, candidate.surname].filter(Boolean).join(' ');
    const label = [nameParts || candidate.id, candidate.postcode].filter(Boolean).join(' — ');

    return (
        <div className="flex items-center justify-between p-2 rounded border border-mp-warning/20 bg-mp-warning-content/50">
            <span className="text-sm text-mp-warning">{label}</span>
            {canReadCustomers && (
                <Button type="button" size="sm" onClick={onSelect}>
                    Select
                </Button>
            )}
        </div>
    );
};

interface DuplicateCustomerNoticeProps {
    duplicateError: DuplicateInScopeError;
    canReadCustomers: boolean;
    onSelectExisting: (customerId: string) => void;
    onEditEmail: () => void;
}

export const DuplicateCustomerNotice: React.FC<DuplicateCustomerNoticeProps> = ({
    duplicateError,
    canReadCustomers,
    onSelectExisting,
    onEditEmail,
}) => {
    const { existingCustomers } = duplicateError;

    return (
        <Alert variant="warning">
            <div>
                <h3 className="font-medium mb-2">
                    {existingCustomers.length === 1
                        ? 'A customer with this email already exists for your merchant.'
                        : `${existingCustomers.length} customers with this email already exist for your merchants.`}
                </h3>
                {existingCustomers.length === 1 ? (
                    <div className="flex gap-2">
                        {canReadCustomers && (
                            <Button
                                type="button"
                                size="sm"
                                onClick={() => onSelectExisting(existingCustomers[0].id)}
                            >
                                Select existing customer
                            </Button>
                        )}
                        <Button type="button" size="sm" variant="clear" onClick={onEditEmail}>
                            Edit email
                        </Button>
                    </div>
                ) : (
                    <>
                        <p className="mb-2">
                            {canReadCustomers
                                ? 'Select an existing customer to continue, or edit the email to create a new one.'
                                : 'Edit the email to create a new customer.'}
                        </p>
                        <ul className="space-y-1 mb-3">
                            {existingCustomers.map((c) => (
                                <li key={c.id}>
                                    <DuplicateCandidateRow
                                        candidate={c}
                                        canReadCustomers={canReadCustomers}
                                        onSelect={() => onSelectExisting(c.id)}
                                    />
                                </li>
                            ))}
                        </ul>
                        <Button type="button" size="sm" variant="clear" onClick={onEditEmail}>
                            Edit email
                        </Button>
                    </>
                )}
            </div>
        </Alert>
    );
};
